(() => {
  'use strict';

  const controllerKey = '__club13StudioHomeController';
  if (window[controllerKey]) {
    window[controllerKey].init(document);
    return;
  }

  const instances = new WeakMap();

  const setupRail = (home) => {
    if (instances.has(home)) return;

    const rail = home.querySelector('[data-studio-rail]');
    const slides = Array.from(home.querySelectorAll('[data-studio-slide]'));
    const previous = home.querySelector('[data-studio-previous]');
    const next = home.querySelector('[data-studio-next]');
    const status = home.querySelector('[data-studio-status]');
    const manualControls = [previous, next].filter(Boolean);
    if (!rail) return;
    if (slides.length < 2) manualControls.forEach((control) => { control.disabled = true; });
    if (slides.length === 0) return;
    const originalControlDisabled = new Map(manualControls.map((control) => [control, control.disabled === true]));
    let order = slides.map((_, index) => index);
    let drag = null;
    let suppressClick = false;
    let wheelLocked = false;
    let wheelBank = 0;
    let wheelTimer = 0;
    let resizeFrame = 0;
    let observer;
    let editorSelectedSlide = null;
    let slots = [];
    let baseCardWidth = 0;

    const isEditorLocked = () => editorSelectedSlide !== null;

    const isNarrow = () => window.matchMedia('(max-width: 640px)').matches;
    const slotsFor = (count, width, height) => {
      const narrow = isNarrow();
      const base = Math.min(width * (narrow ? 0.64 : 0.42), height);
      const decay = narrow ? 0.72 : 0.62;
      const minimum = narrow ? 0.32 : 0.18;
      const scaleAt = (step) => Math.max(minimum, Math.pow(decay, step));
      const fadeAt = (step) => Math.max(0.38, 1 - step * 0.1);
      const slots = new Array(count);
      const center = (width - base) / 2;
      slots[0] = { left: center, width: base, opacity: 1, z: 100 };

      const rightCount = Math.ceil((count - 1) / 2);
      let left = center;
      let previousWidth = base;
      for (let step = 1; step <= rightCount; step += 1) {
        const slotWidth = base * scaleAt(step);
        left += previousWidth * 0.85;
        slots[step] = { left, width: slotWidth, opacity: fadeAt(step), z: 100 - step };
        previousWidth = slotWidth;
      }

      let right = center + base;
      previousWidth = base;
      for (let step = 1; step <= count - 1 - rightCount; step += 1) {
        const slotWidth = base * scaleAt(step);
        right -= previousWidth * 0.85;
        slots[count - step] = { left: right - slotWidth, width: slotWidth, opacity: fadeAt(step), z: 100 - step };
        previousWidth = slotWidth;
      }
      return slots;
    };

    const render = () => {
      if (!slots.length || !baseCardWidth) return;
      slides.forEach((slide, itemIndex) => {
        const ringPosition = order.indexOf(itemIndex);
        const slot = slots[ringPosition];
        const active = ringPosition === 0;
        slide.style.setProperty('--studio-rail-slot-left', `${slot.left}px`);
        slide.style.setProperty('--studio-rail-slot-scale', String(slot.width / baseCardWidth));
        slide.style.opacity = String(slot.opacity);
        slide.style.zIndex = String(slot.z);
        slide.classList.toggle('is-active', active);
        slide.removeAttribute('aria-hidden');
        const linked = slide.hasAttribute('href');
        if (active && linked) slide.removeAttribute('role');
        else slide.setAttribute('role', active ? 'group' : 'button');
        slide.setAttribute('aria-label', `${active ? linked ? 'Shop ' : '' : 'Show '}${slide.dataset.title || 'product'}`);
        slide.tabIndex = active ? 0 : -1;
      });
      const active = slides[order[0]];
      const title = active && active.dataset.title ? active.dataset.title : '';
      if (status) status.textContent = `${String(order[0] + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}${title ? ` · ${title}` : ''}`;
    };

    const measure = () => {
      const bounds = rail.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      slots = slotsFor(slides.length, bounds.width, bounds.height);
      baseCardWidth = slots[0]?.width || 0;
      rail.style.setProperty('--studio-rail-base-card-width', `${baseCardWidth}px`);
      render();
    };

    const rotateBy = (direction, steps = 1, focusActive = false) => {
      if (slides.length < 2 || isEditorLocked()) return;
      const count = steps % slides.length;
      if (!count) return;
      order = direction > 0
        ? [...order.slice(count), ...order.slice(0, count)]
        : [...order.slice(-count), ...order.slice(0, -count)];
      render();
      if (focusActive) slides[order[0]].focus({ preventScroll: true });
    };
    const rotate = (direction, focusActive = false) => rotateBy(direction, 1, focusActive);
    const selectSlide = (slide) => {
      if (isEditorLocked()) return;
      const position = order.indexOf(slides.indexOf(slide));
      if (position <= 0) return;
      order = [...order.slice(position), ...order.slice(0, position)];
      render();
      slide.focus({ preventScroll: true });
    };

    const onPrevious = () => rotate(-1);
    const onNext = () => rotate(1);
    const onKeydown = (event) => {
      if (isEditorLocked()) {
        if (['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
        }
        return;
      }
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        rotate(1, true);
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        rotate(-1, true);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        const target = event.key === 'Home' ? 0 : slides.length - 1;
        const position = order.indexOf(target);
        order = [...order.slice(position), ...order.slice(0, position)];
        render();
        slides[order[0]].focus({ preventScroll: true });
      } else if (event.key === ' ' || event.key === 'Enter') {
        const slide = event.target instanceof Element ? event.target.closest('[data-studio-slide]') : null;
        if (slide && slides.includes(slide) && order[0] !== slides.indexOf(slide)) {
          event.preventDefault();
          selectSlide(slide);
        }
      }
    };
    const onWheel = (event) => {
      if (isEditorLocked()) return;
      const horizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : 0;
      if (!horizontal) return;
      event.preventDefault();
      if (wheelLocked) return;
      wheelBank += horizontal;
      if (Math.abs(wheelBank) >= 100) {
        rotate(wheelBank > 0 ? 1 : -1);
        wheelBank = 0;
        wheelLocked = true;
        wheelTimer = window.setTimeout(() => { wheelLocked = false; }, 260);
      }
    };
    const onPointerDown = (event) => {
      if (isEditorLocked() || !event.isPrimary || event.button !== 0 || drag) return;
      suppressClick = false;
      // Keep capture on the original card: clicks still reach that card, and dragging continues
      // when the card rotates or the pointer leaves the gallery. Native link dragging is disabled.
      const capture = event.target instanceof Element ? event.target.closest('[data-studio-slide]') || rail : rail;
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false, vertical: false, delta: 0, applied: 0, capture };
      capture.setPointerCapture(event.pointerId);
    };
    const onPointerMove = (event) => {
      if (isEditorLocked()) {
        drag = null;
        return;
      }
      if (!drag || drag.id !== event.pointerId) return;
      const horizontal = event.clientX - drag.x;
      const vertical = event.clientY - drag.y;
      if (!drag.moved && !drag.vertical && Math.max(Math.abs(horizontal), Math.abs(vertical)) > 8) {
        if (Math.abs(horizontal) > Math.abs(vertical)) {
          drag.moved = true;
          rail.classList.add('is-dragging');
        } else {
          drag.vertical = true;
        }
      }
      if (!drag.moved) return;
      event.preventDefault();
      drag.delta = horizontal;
      const steps = Math.trunc(horizontal / 88);
      const changedSteps = steps - drag.applied;
      if (changedSteps) {
        rotateBy(changedSteps > 0 ? -1 : 1, Math.abs(changedSteps));
        drag.applied = steps;
      }
    };
    const finishDrag = (event) => {
      if (isEditorLocked()) {
        drag = null;
        return;
      }
      if (!drag || drag.id !== event.pointerId) return;
      suppressClick = drag.moved || drag.vertical;
      if (event.type === 'pointerup' && drag.moved) {
        if (drag.applied === 0 && Math.abs(drag.delta) > 8) rotate(drag.delta < 0 ? 1 : -1);
      }
      drag = null;
      rail.classList.remove('is-dragging');
    };
    const onClick = (event) => {
      // Suppress only the drag's trailing pointer click. A new pointerdown or keyboard activation
      // must work immediately, including when the browser did not emit a click after dragging.
      if (suppressClick && event.detail !== 0) {
        suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (isEditorLocked()) return;
      const slide = event.target instanceof Element ? event.target.closest('[data-studio-slide]') : null;
      if (slide && slides.includes(slide) && order[0] !== slides.indexOf(slide)) {
        event.preventDefault();
        selectSlide(slide);
      }
    };
    const onDragStart = (event) => event.preventDefault();
    const resetManualInteraction = () => {
      const previousDrag = drag;
      drag = null;
      if (previousDrag) {
        try {
          if (previousDrag.capture.hasPointerCapture(previousDrag.id)) previousDrag.capture.releasePointerCapture(previousDrag.id);
        } catch (_) { /* pointer capture may already be released */ }
      }
      rail.classList.remove('is-dragging');
      suppressClick = false;
      wheelBank = 0;
      wheelLocked = false;
      window.clearTimeout(wheelTimer);
      wheelTimer = 0;
    };
    const setEditorControlState = (locked) => {
      manualControls.forEach((control) => {
        control.disabled = locked || originalControlDisabled.get(control);
      });
    };
    const resize = () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(measure);
    };

    const blockIdForSlide = (slide) => {
      const metadata = slide.getAttribute('data-shopify-editor-block');
      if (!metadata) return null;
      try {
        return JSON.parse(metadata)?.id || null;
      } catch (_) {
        return null;
      }
    };
    const slideForBlockEvent = (event) => {
      const eventTarget = event.target instanceof Element ? event.target : null;
      const targetSlide = eventTarget?.closest('[data-studio-slide]');
      if (targetSlide && slides.includes(targetSlide)) return targetSlide;

      const blockId = event.detail?.blockId;
      if (!blockId) return null;
      return slides.find((slide) => blockIdForSlide(slide) === blockId) || null;
    };
    const onBlockSelect = (event) => {
      if (!window.Shopify?.designMode) return;
      const slide = slideForBlockEvent(event);
      if (!slide) return;

      resetManualInteraction();
      editorSelectedSlide = slide;
      setEditorControlState(true);
      const itemIndex = slides.indexOf(slide);
      const position = order.indexOf(itemIndex);
      if (position > 0) {
        order = [...order.slice(position), ...order.slice(0, position)];
      }
      render();
      slide.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    };
    const onBlockDeselect = (event) => {
      if (!editorSelectedSlide) return;
      const slide = slideForBlockEvent(event);
      if (slide !== editorSelectedSlide) return;
      editorSelectedSlide = null;
      setEditorControlState(false);
    };

    previous?.addEventListener('click', onPrevious);
    next?.addEventListener('click', onNext);
    rail.addEventListener('keydown', onKeydown);
    rail.addEventListener('wheel', onWheel, { passive: false });
    rail.addEventListener('pointerdown', onPointerDown);
    rail.addEventListener('pointermove', onPointerMove);
    rail.addEventListener('pointerup', finishDrag);
    rail.addEventListener('pointercancel', finishDrag);
    rail.addEventListener('lostpointercapture', finishDrag);
    rail.addEventListener('dragstart', onDragStart);
    rail.addEventListener('click', onClick, true);
    if (window.Shopify?.designMode) {
      home.addEventListener('shopify:block:select', onBlockSelect);
      home.addEventListener('shopify:block:deselect', onBlockDeselect);
    }
    if ('ResizeObserver' in window) {
      observer = new ResizeObserver(resize);
      observer.observe(rail);
    } else {
      window.addEventListener('resize', resize);
    }
    instances.set(home, () => {
      resetManualInteraction();
      setEditorControlState(false);
      previous?.removeEventListener('click', onPrevious);
      next?.removeEventListener('click', onNext);
      rail.removeEventListener('keydown', onKeydown);
      rail.removeEventListener('wheel', onWheel);
      rail.removeEventListener('pointerdown', onPointerDown);
      rail.removeEventListener('pointermove', onPointerMove);
      rail.removeEventListener('pointerup', finishDrag);
      rail.removeEventListener('pointercancel', finishDrag);
      rail.removeEventListener('lostpointercapture', finishDrag);
      rail.removeEventListener('dragstart', onDragStart);
      rail.removeEventListener('click', onClick, true);
      if (window.Shopify?.designMode) {
        home.removeEventListener('shopify:block:select', onBlockSelect);
        home.removeEventListener('shopify:block:deselect', onBlockDeselect);
      }
      observer?.disconnect();
      window.removeEventListener('resize', resize);
      window.cancelAnimationFrame(resizeFrame);
      window.clearTimeout(wheelTimer);
      instances.delete(home);
    });
    measure();
    rail.classList.add('is-ready');
  };

  const homesWithin = (root) => {
    if (!root || root.nodeType !== Node.ELEMENT_NODE) return [];
    const homes = [];
    if (root.matches?.('[data-studio-home]')) homes.push(root);
    homes.push(...root.querySelectorAll?.('[data-studio-home]') || []);
    return homes;
  };
  const init = (root = document) => {
    if (root === document) document.querySelectorAll('[data-studio-home]').forEach(setupRail);
    else homesWithin(root).forEach(setupRail);
  };
  const destroy = (root) => homesWithin(root).forEach((home) => instances.get(home)?.());

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  window[controllerKey] = { init, destroy };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
